import { useState } from 'react'
import { Rechenabbruch } from './api'
import { Dialogfenster } from './Dialogfenster'
import { Periodenfeld } from './Periodenfeld'
import { periodenname, satz, t } from './sprache'

/**
 * Die Rückfrage nach dem Monat, ab dem eine neue Zuordnung wirkt
 * (5.1 Abschnitt 3.51, R1171, R1173).
 *
 * **Der Server ist das Netz, nicht die Oberfläche.** War ein gelieferter Wert
 * schon einmal zugeordnet, weist der Server jede neue Zuordnung ohne Monat ab
 * und nennt dabei die frühere. Diese Stelle fängt genau diese Absage ab und
 * fragt nach dem Monat — so gilt die Regel auf jedem Weg, auf dem zugeordnet
 * wird: im Zuordnungsfenster, an der Vorschau eines Datenimports und unter
 * *Vorbereiten*.
 *
 * **Das Feld ist leer** (R1171): Wer zuordnet, was schon einmal galt,
 * entscheidet über die Vergangenheit, und das soll er nicht durch Bestätigen
 * einer Vorgabe tun.
 */

/** Die Absage, die nach dem Monat fragt — sonst `null`. */
export function monatFehlt(f: unknown): Rechenabbruch | null {
  return f instanceof Rechenabbruch && f.meldung.schluessel.startsWith('import.zuordnung.monatfehlt.')
    ? f
    : null
}

/**
 * Fragt nach dem Monat und reicht ihn an den Handgriff zurück, der ihn
 * gebraucht hat. Der Handgriff selbst schreibt dann mit Monat — und meldet
 * auch selbst, wenn es danach noch schiefgeht.
 */
export function useMonatsfrage() {
  const [frage, setFrage] = useState<{ absage: Rechenabbruch; weiter: (ab: number) => void } | null>(null)

  const fenster = frage && (
    <Wirksamkeitsfrage
      absage={frage.absage}
      aufWirksam={(ab) => {
        const weiter = frage.weiter
        setFrage(null)
        weiter(ab)
      }}
      aufSchliessen={() => setFrage(null)}
    />
  )

  return {
    /** Öffnet die Rückfrage; `weiter` bekommt den eingetragenen Monat. */
    frage: (absage: Rechenabbruch, weiter: (ab: number) => void) => setFrage({ absage, weiter }),
    fenster,
  }
}

function Wirksamkeitsfrage({
  absage,
  aufWirksam,
  aufSchliessen,
}: {
  absage: Rechenabbruch
  aufWirksam: (ab: number) => void
  aufSchliessen: () => void
}) {
  const [ab, setAb] = useState<number | null>(null)

  return (
    <Dialogfenster
      titel={t('mapping.wirksam.titel')}
      weite="schmal"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          <button type="button" className="haupt" disabled={ab === null} onClick={() => ab !== null && aufWirksam(ab)}>
            {ab === null ? t('mapping.wirksam.tun.leer') : t('mapping.wirksam.tun', { monat: periodenname(ab) })}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('loeschen.zurueck')}
          </button>
        </>
      }
    >
      <p>{satz(absage.meldung)}</p>
      <Periodenfeld
        wert={ab}
        aufWert={setAb}
        beschriftung={t('mapping.wirksam.ab')}
        klasse=""
        erlaeuterung={absage.handreichung ? satz(absage.handreichung) : undefined}
      />
    </Dialogfenster>
  )
}
